import {
  alpha,
  Autocomplete,
  Box,
  Breadcrumbs,
  Grid,
  Skeleton,
  Stack,
  styled,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoPopover from "../../Components/Common/poper/InfoPopover";
import { useEffect, useRef, useState } from "react";
import DataTable from "../../Components/Common/datatable/DataTable";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import ButtonComponent from "../../Components/Common/button/Button";
import { setBottlerData } from "../../redux/entitySlice";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import EditIcon from "@mui/icons-material/Edit";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useSelector } from "react-redux";
import { useSupport } from "../../context/SupportContext";

function PlantConfiguration() {
  const gridRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [effectivePeriod, setEffectivePeriod] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  let { combinationDetails, setCombinationDetails } = useSupport();
  const location = useLocation();

  const BottlerName = useSelector(
    (state) => state.user.selectedBottler?.bottler
  );
  const navigate = useNavigate();
  const data = location.state?.data;

  useEffect(() => {
    fetchUserDetails();
  }, [data]);

  useEffect(() => {
    effectivePeriods();
  }, []);

  const fetchUserDetails = async () => {
    if (!data?.customerId) return;
    try {
      setLoading(true);
      const response = await ApiService.get(
        `/v1/Onboard/getRollingIntervalConfigs?customerId=${data.customerId}`
      );
      setFilteredData(response.data.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  const effectivePeriods = async () => {
    try {
      const response = await ApiService.get(
        `/v1/Onboard/get-effective-periods`
      );
      setEffectivePeriod(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    const resizeGrid = () => {
      if (gridRef.current && gridRef.current.api && filteredData.length > 0) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    resizeGrid();

    window.addEventListener("resize", resizeGrid);
    return () => window.removeEventListener("resize", resizeGrid);
  }, [filteredData]);

  const PlantColumnDefinition = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      field: "supportCheckbox",
      headerName: "",
      sortable: false,
      width: 50,
      suppressSizeToFit: true,
    },
    {
      field: "locationName", // Plant Column
      headerName: "Plant",
      sortable: true,
      unSortIcon: true,
      width: 300,
      minWidth: 300,
      valueGetter: (params) => {
        const { locationId, locationName } = params.data;
        return `${locationId || ""} - ${locationName || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      headerName: "Rolling Week Submission",
      headerGroupClass: "group-header-center",
      children: [
        {
          field: "shortTermActive", // renamed from rollingWeekEnable
          headerName: "Enable",
          width: 130,
          minWidth: 130,
          sortable: true,
          unSortIcon: true,
          editable: true,
          valueSetter: (params) => {
            const newValue =
              params.newValue === true || params.newValue === "true";
            if (!newValue) {
              params.data.shortTermPeriodCount = null;
            }
            params.data.shortTermActive = newValue;
            return true;
          },
          cellRendererParams: {
            useFormatter: false,
          },
        },
        {
          field: "shortTermPeriodCount", // renamed from forecastDuration
          headerName: "Forecast Duration (weeks)",
          width: 270,
          minWidth: 270,
          sortable: true,
          unSortIcon: true,
          cellClass: "red-border",
          filter: "agMultiColumnFilter",
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
              },
              {
                filter: "agSetColumnFilter",
              },
            ],
          },
          editable: (params) => {
            return params.data && params.data.shortTermActive === true;
          },
          cellRenderer: (params) => {
            if (!params.data.shortTermActive) {
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <span>Enter a duration (12-20 weeks)</span>
                </div>
              );
            }
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "black",
                }}
              >
                <span>{params.value || ""}</span>
              </div>
            );
          },
          valueSetter: (params) => {
            const newValue = params.newValue;
            const numericValue = parseFloat(newValue);

            if (isNaN(numericValue)) {
              setTimeout(() => {
                MessagePopup({
                  icon: "error",
                  title: "Invalid Input",
                  message: "Please enter a valid numeric value.",
                });
              }, 0);
              return false;
            }

            if (numericValue < 12 || numericValue > 20) {
              setTimeout(() => {
                MessagePopup({
                  icon: "error",
                  title: "Invalid Range",
                  message: "Please enter a value between 12 and 20.",
                });
              }, 0);
              return false;
            }
            params.data.shortTermPeriodCount = numericValue;
            return true;
          },
        },
      ],
    },
    {
      headerName: "Flexible Long Term Forecasts",
      headerGroupClass: "group-header-center",
      children: [
        {
          field: "longTermActive", // renamed from longTermEnable
          headerName: "Enable",
          width: 130,
          minWidth: 130,
          editable: true,
          sortable: true,
          unSortIcon: true,
          valueSetter: (params) => {
            const newValue =
              params.newValue === true || params.newValue === "true";
            if (!newValue) {
              params.data.longTermInterval = null;
              params.data.effectivePeriod = null; // Clear effectivePeriod when not active
            }
            params.data.longTermActive = newValue;
            return true;
          },
        },
        {
          field: "longTermInterval",
          headerName: "Submission Frequency (months)",
          cellClass: "red-border",
          width: 285,
          minWidth: 285,
          sortable: true,
          unSortIcon: true,
          editable: (params) => {
            return params.data && params.data.longTermActive === true;
          },
          cellRenderer: (params) => {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>
                  {params.data && params.data.longTermActive
                    ? params.value || "Select Months"
                    : "Select Months"}
                </span>
              </div>
            );
          },

          cellEditor: "agSelectCellEditor",
          cellEditorParams: (params) => {
            // Dynamically set values based on whether longTermInterval is numeric or null
            const isNumeric = typeof params.data.longTermInterval === "number";
            return {
              values: isNumeric ? [2, 3] : ["2", "3"], // Numeric values if longTermInterval has a value, else string values
            };
          },
        },
        {
          field: "effectivePeriod",
          headerName: "Effective Period",
          editable: (params) => {
            // Edit only if 'longTermActive' is true
            return params.data && params.data.longTermActive === true;
          },
          resizable: false,
          width: 200,
          minWidth: 200,
          cellClass: "red-border",
          cellEditor: "agSelectCellEditor",
          cellEditorParams: () => ({
            values: effectivePeriod || [], // Dynamically pass available options
          }),
          cellRenderer: (params) => {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span>
                  {params.data && params.data.longTermActive
                    ? params.value || "Select Effective Period"
                    : "Select Effective Period"}
                </span>
              </div>
            );
          },
          valueSetter: (params) => {
            const value = params.newValue;
            if (!value) {
              return false; // Ignore invalid
            }

            params.data.effectivePeriod = value;
            return true;
          },
        },
      ],
    },
  ];

  const plantpagination = true;
  const plantpaginationPageSize = 10;
  const plantpaginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onCellValueChanged = (params) => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({ force: true });
      gridRef.current.api.sizeColumnsToFit();
    }

    params.data.isDataChanged = true;
    setHasUnsavedChanges(true);
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    const combinationDetails = selected.map((row) => ({
      bottler: BottlerName ?? "",
      locationName: row.locationName ?? "",
      customerName: `${data?.customerId} - ${data?.customerName}` ?? "",
    }));

    setCombinationDetails(combinationDetails);
  };

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  // const getRowStyle = (params) => {
  //   if (params.node && params.node.isSelected && params.node.isSelected()) {
  //     return {
  //       border: "1.5px solid red",
  //       borderRadius: "4px",
  //     };
  //   }
  //   return {};
  // };

  const getRowStyle = (params) => {
    const styles = {};
    if (params.data.isDataChanged) {
      styles.border = "1.5px solid red";
      styles.borderRadius = "4px";
    }
    return styles;
  };

  const onSaveHandler = async () => {
    const selectedRows = filteredData.filter((row) => row.isDataChanged);

    const invalidRow = selectedRows.find((row) => {
      // Check for shortTermActive validation
      if (
        row.shortTermActive &&
        (row.shortTermPeriodCount === null || row.shortTermPeriodCount === "")
      ) {
        return true; // Invalid if shortTermPeriodCount is not set when shortTermActive is true
      }

      // Check for longTermActive validation
      if (
        row.longTermActive &&
        (row.longTermInterval === null ||
          row.longTermInterval === "" ||
          row.effectivePeriod === null ||
          row.effectivePeriod === "")
      ) {
        return true; // Invalid if longTermInterval or effectivePeriod is not set when longTermActive is true
      }

      return false; // Row is valid
    });

    if (invalidRow) {
      // Show error message for invalid data
      MessagePopup({
        icon: "error",
        title: "Invalid Data",
        message: `Missing Forecast Duration or Submission Frequency or Effective Period for Plant ${invalidRow.locationName}`,
      });
      return;
    }
    const result = await MessagePopup({
      title: "Confirm Save",
      html: `<p>Are you sure want to save the Configuration?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });
    if (!result.isConfirmed) return;

    const requestBody = selectedRows.map((row) => ({
      customerId: row.customerId,
      customerName: row.customerName,
      locationId: row.locationId,
      locationName: row.locationName,
      shortTermPeriodCount:
        row.shortTermActive === false ? null : row.shortTermPeriodCount,
      shortTermActive: row.shortTermActive,
      longTermInterval:
        row.longTermActive === false ? null : row.longTermInterval,
      longTermActive: row.longTermActive,
      effectivePeriod:
        row.longTermActive === false ? null : row.effectivePeriod,
    }));

    try {
      const response = await ApiService.post(
        `/v1/Onboard/save-customer-location-config`,
        requestBody
      );

      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Configuration saved successfully.",
      });
      setHasUnsavedChanges(false);

      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.refreshCells({ force: true });
      }
      setCombinationDetails([]);
      gridRef.current.api.deselectAll();

      fetchUserDetails();
    } catch (error) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to save configuration. Please try again.",
      });
    }
  };

  const defaultMessage = "No Plant Associated with Particular Customer";

  return (
    <>
      <div>
        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 2, mt: 2 }}
          >
            <Link
              href="/bottler-onboarding"
              onClick={(e) => {
                e.preventDefault();
                navigate("/bottler-onboarding");
              }}
              style={{ color: "black", textDecoration: "underline" }}
            >
              Bottler Configuration
            </Link>
            <Link
              href="/partner-config"
              onClick={(e) => {
                e.preventDefault();
                navigate("/partner-config");
              }}
              style={{ color: "black", textDecoration: "underline" }}
            >
              Forecast Partner Configuration
            </Link>

            <Typography color="text.primary">Plant Configuration</Typography>
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Plant Configuration
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Box>
            <ButtonComponent
              bgColor="black"
              color="white"
              label="Save"
              fullWidth={false}
              onClick={onSaveHandler}
              disabled={!hasUnsavedChanges}
            />
          </Box>
          <Stack
            direction="row"
            spacing={3}
            sx={{ alignItems: "center", marginBottom: "10px" }}
          >
            <Box>Bottler: {BottlerName || ""}</Box>
            <Box>
              Forecast Partner: {`${data?.customerId} - ${data?.customerName}`}
            </Box>
          </Stack>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={"67vh"}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : !filteredData.some(
              (row) => row.locationId && row.locationName
            ) ? (
            // Show a fallback "No Data" message if filteredData is empty
            <div
              style={{
                textAlign: "left",
                fontSize: "20px",
                color: "black",
                paddingTop: "20px",
                paddingBottom: "20px",
                marginTop: "10px",
              }}
            >
              You do not have permission to access plants for the selected
              Forecast Partner.
            </div>
          ) : (
            <DataTable
              ref={gridRef}
              columns={PlantColumnDefinition}
              rows={filteredData || []}
              tableHeight={"67vh"}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              pagination={plantpagination}
              paginationPageSize={plantpaginationPageSize}
              paginationPageSizeSelector={plantpaginationPageSizeSelector}
              onGridReady={onGrid}
              rowSelection="multiple"
              getRowStyle={getRowStyle}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={onSelectionChanged}
              singleClickEdit={true}
            />
          )}
        </Box>
      </div>
    </>
  );
}

export default PlantConfiguration;
